import { PhotoCrosshair } from 'features/kestrelViewer'
import { usePhotoUrl } from 'features/kestrelViewer/ui/tools/photoreel/hooks/usePhotoUrl'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { KVPhoto } from 'types'

import { PhotoViewerToolbar } from './PhotoViewerToolbar'

const ImageContainer = styled.div<{ $fillContainer?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  ${({ $fillContainer }) =>
    $fillContainer &&
    `
    width: 100%;
    height: 100%;
  `}
`

const FullImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: transform 0.1s ease-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
`

const HighResLoadingIndicator = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 24px;
  height: 24px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  z-index: 10;
  pointer-events: none;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #87ceeb;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  gap: 8px;
`

const ErrorText = styled.div`
  font-size: 14px;
  opacity: 0.6;
`

interface PhotoViewerProps {
  photo: KVPhoto
  rotation: number
  onRotationChange: (rotation: number) => void
  /** If true, fills the parent container. If false, uses explicit dimensions */
  fillContainer?: boolean
  /** Explicit container dimensions (used when fillContainer is false) */
  containerWidth?: number
  containerHeight?: number
  /** Compact toolbar mode for smaller containers */
  compactToolbar?: boolean
  /** Show crosshair at projection point */
  showCrosshair?: boolean
  /** Called when photo changes to reset external state */
  onPhotoChange?: () => void
  /** Optional close callback - shows close button in toolbar when provided */
  onClose?: () => void
}

export const PhotoViewer: React.FC<PhotoViewerProps> = ({
  photo,
  rotation,
  onRotationChange,
  fillContainer = false,
  containerWidth,
  containerHeight,
  compactToolbar = false,
  showCrosshair = true,
  onPhotoChange,
  onClose,
}) => {
  // Viewport state for zoom/pan
  const [viewport, setViewport] = useState({
    zoom: 1,
    pan: { x: 0, y: 0 },
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    hasMoved: false,
  })

  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const {
    url: currentImageUrl,
    isLoading: urlLoading,
    isLoadingHighRes,
    error: urlError,
  } = usePhotoUrl(photo, 'fullSize')

  const isImageLoading = urlLoading || !currentImageUrl

  // Reset viewport and dimensions when photo changes
  useEffect(() => {
    setViewport({
      zoom: 1,
      pan: { x: 0, y: 0 },
      isDragging: false,
      dragStart: { x: 0, y: 0 },
      hasMoved: false,
    })
    setImageDimensions(null)
    onPhotoChange?.()
  }, [photo.id, onPhotoChange])

  const rotateClockwise = useCallback(() => {
    onRotationChange(rotation + 90)
  }, [rotation, onRotationChange])

  // Calculate container dimensions accounting for rotation
  const getContainerDimensions = useCallback(() => {
    // If filling container, use parent dimensions or fallback
    if (fillContainer) {
      const width = containerWidth ?? 400
      const height = containerHeight ?? 300
      return { width: `${width}px`, height: `${height}px` }
    }

    // Modal mode: calculate based on viewport
    const maxWidth = window.innerWidth * 0.88 - 32
    const maxHeight = window.innerHeight * 0.88 - 64

    if (!imageDimensions) {
      return {
        width: `${maxWidth}px`,
        height: `${maxHeight}px`,
      }
    }

    // When rotated 90° or 270°, the effective dimensions swap
    const normalizedRotation = rotation % 360
    const isRotated90or270 =
      normalizedRotation === 90 || normalizedRotation === 270
    const effectiveWidth = isRotated90or270
      ? imageDimensions.height
      : imageDimensions.width
    const effectiveHeight = isRotated90or270
      ? imageDimensions.width
      : imageDimensions.height

    const imageAspectRatio = effectiveWidth / effectiveHeight
    const maxAspectRatio = maxWidth / maxHeight

    const [calcWidth, calcHeight] =
      imageAspectRatio > maxAspectRatio
        ? [maxWidth, maxWidth / imageAspectRatio]
        : [maxHeight * imageAspectRatio, maxHeight]

    return {
      width: `${calcWidth}px`,
      height: `${calcHeight}px`,
    }
  }, [
    imageDimensions,
    rotation,
    fillContainer,
    containerWidth,
    containerHeight,
  ])

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    if (!imageDimensions) {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.95 : 1.05

    setViewport((prev) => {
      const newZoom = Math.min(Math.max(prev.zoom * delta, 1), 5)
      const zoomRatio = newZoom / prev.zoom
      const scaledPan = { x: prev.pan.x * zoomRatio, y: prev.pan.y * zoomRatio }

      const containerDims = getContainerDimensions()
      const maxPanX =
        ((newZoom - 1) * (parseInt(containerDims.width) || 200)) / 2
      const maxPanY =
        ((newZoom - 1) * (parseInt(containerDims.height) || 200)) / 2

      return {
        ...prev,
        zoom: newZoom,
        pan: {
          x:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.x, -maxPanX), maxPanX),
          y:
            newZoom === 1
              ? 0
              : Math.min(Math.max(scaledPan.y, -maxPanY), maxPanY),
        },
      }
    })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewport.zoom <= 1) return
    setViewport((prev) => ({
      ...prev,
      isDragging: true,
      hasMoved: false,
      dragStart: { x: e.clientX - prev.pan.x, y: e.clientY - prev.pan.y },
    }))
  }

  const handleDragStart = (e: React.DragEvent) => {
    e.preventDefault()
  }

  // Calculate crosshair position accounting for zoom, pan, and rotation
  const getCrosshairPosition = () => {
    const projection = photo.projection
    if (!projection || !imageDimensions || !showCrosshair) return null

    const containerDimensions = getContainerDimensions()
    const cWidth = parseInt(containerDimensions.width as string) || 0
    const cHeight = parseInt(containerDimensions.height as string) || 0

    const containerAspectRatio = cWidth / cHeight
    const originalAspectRatio = imageDimensions.width / imageDimensions.height

    let scaledImageWidth: number
    let scaledImageHeight: number
    let offsetX: number
    let offsetY: number

    if (originalAspectRatio > containerAspectRatio) {
      scaledImageWidth = cWidth
      scaledImageHeight = cWidth / originalAspectRatio
      offsetX = 0
      offsetY = (cHeight - scaledImageHeight) / 2
    } else {
      scaledImageHeight = cHeight
      scaledImageWidth = cHeight * originalAspectRatio
      offsetX = (cWidth - scaledImageWidth) / 2
      offsetY = 0
    }

    const imageRelativeX = projection.x / projection.originalImageWidth
    const imageRelativeY = projection.y / projection.originalImageHeight

    const baseX = offsetX + imageRelativeX * scaledImageWidth
    const baseY = offsetY + imageRelativeY * scaledImageHeight

    const centerX = cWidth / 2
    const centerY = cHeight / 2

    const rotationRad = (rotation * Math.PI) / 180
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)
    const relX = baseX - centerX
    const relY = baseY - centerY
    const rotatedX = relX * cos - relY * sin
    const rotatedY = relX * sin + relY * cos

    const scaledX = centerX + rotatedX * viewport.zoom
    const scaledY = centerY + rotatedY * viewport.zoom

    const finalX = scaledX + viewport.pan.x
    const finalY = scaledY + viewport.pan.y

    return { x: finalX, y: finalY }
  }

  const crosshairPosition = getCrosshairPosition()
  const projection = photo.projection

  // Handle global mouse events during dragging
  useEffect(() => {
    if (!viewport.isDragging) return

    const handleGlobalMouseMove = (e: MouseEvent) => {
      setViewport((prev) => {
        const newPan = {
          x: e.clientX - prev.dragStart.x,
          y: e.clientY - prev.dragStart.y,
        }

        const containerDims = getContainerDimensions()
        const maxPanX =
          ((prev.zoom - 1) * (parseInt(containerDims.width) || 200)) / 2
        const maxPanY =
          ((prev.zoom - 1) * (parseInt(containerDims.height) || 200)) / 2

        return {
          ...prev,
          hasMoved: true,
          pan: {
            x: Math.min(Math.max(newPan.x, -maxPanX), maxPanX),
            y: Math.min(Math.max(newPan.y, -maxPanY), maxPanY),
          },
        }
      })
    }

    const handleGlobalMouseUp = () => {
      setViewport((prev) => ({ ...prev, isDragging: false }))
      setTimeout(() => {
        setViewport((prev) => ({ ...prev, hasMoved: false }))
      }, 100)
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [
    viewport.isDragging,
    viewport.dragStart,
    viewport.zoom,
    getContainerDimensions,
  ])

  const containerStyle = fillContainer ? undefined : getContainerDimensions()

  return (
    <ImageContainer style={containerStyle} $fillContainer={fillContainer}>
      {!isImageLoading && (
        <PhotoViewerToolbar
          onRotateClockwise={rotateClockwise}
          onClose={onClose}
          compact={compactToolbar}
        />
      )}

      {urlError ? (
        <ErrorContainer>
          <div>Failed to load photo</div>
          <ErrorText>{urlError}</ErrorText>
        </ErrorContainer>
      ) : (
        <>
          {isImageLoading && <LoadingSpinner />}

          {currentImageUrl && (
            <FullImage
              src={currentImageUrl}
              alt='Photo'
              draggable={false}
              onLoad={handleImageLoad}
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              onDragStart={handleDragStart}
              style={{
                display: isImageLoading ? 'none' : 'block',
                transform: `translate(${viewport.pan.x}px, ${viewport.pan.y}px) scale(${viewport.zoom}) rotate(${rotation}deg)`,
                cursor:
                  viewport.zoom > 1
                    ? viewport.isDragging
                      ? 'grabbing'
                      : 'grab'
                    : 'default',
              }}
            />
          )}

          {!isImageLoading && isLoadingHighRes && <HighResLoadingIndicator />}

          {crosshairPosition && !isImageLoading && (
            <PhotoCrosshair
              x={crosshairPosition.x}
              y={crosshairPosition.y}
              scale={1.0}
              originalImageWidth={projection?.originalImageWidth}
              originalImageHeight={projection?.originalImageHeight}
            />
          )}
        </>
      )}
    </ImageContainer>
  )
}
